import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, INTERVAL_LABELS } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Thermometer, Check, Search, Plus, Loader2, CheckCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "heute", label: "Heute fällig", testid: "todo-tab-heute-faellig" },
  { id: "ueberfaellig", label: "Überfällig", testid: "todo-tab-ueberfaellig" },
  { id: "erledigt", label: "Erledigt", testid: "todo-tab-erledigt" },
];

const keyOf = (e) => `${e.task_id}|${e.period_key}`;

function groupByRoom(entries) {
  const map = {};
  entries.forEach((e) => { const k = e.room_name || "—"; (map[k] = map[k] || []).push(e); });
  return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
}

export default function TodoPage() {
  const [tab, setTab] = useState("heute");
  const [todo, setTodo] = useState({ heute: [], ueberfaellig: [] });
  const [erledigt, setErledigt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [spontanOpen, setSpontanOpen] = useState(false);
  const [rooms, setRooms] = useState([]);

  const [selected, setSelected] = useState({});   // key -> true
  const [temps, setTemps] = useState({});          // key -> string
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [t, e, r] = await Promise.all([api.get("/todo"), api.get("/todo/erledigt"), api.get("/rooms")]);
    setTodo(t.data); setErledigt(e.data); setRooms(r.data);
    setSelected({}); setTemps({}); setNote("");
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filterFn = (e) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [e.task_name, e.object_name, e.room_name].some((v) => (v || "").toLowerCase().includes(s));
  };

  const current = useMemo(() => {
    if (tab === "heute") return todo.heute.filter(filterFn);
    if (tab === "ueberfaellig") return todo.ueberfaellig.filter(filterFn);
    return erledigt.filter(filterFn);
  }, [tab, todo, erledigt, search]);

  const counts = { heute: todo.heute.length, ueberfaellig: todo.ueberfaellig.length, erledigt: erledigt.length };
  const isActionTab = tab !== "erledigt";

  const toggle = (e) => setSelected((s) => ({ ...s, [keyOf(e)]: !s[keyOf(e)] }));
  const selectAll = () => {
    const all = {}; current.forEach((e) => { all[keyOf(e)] = true; }); setSelected(all);
  };
  const clearSel = () => setSelected({});

  const selectedEntries = current.filter((e) => selected[keyOf(e)]);

  const saveBulk = async () => {
    if (selectedEntries.length === 0) return;
    // temperature validation
    const missing = selectedEntries.filter((e) => e.type === "temperature" && !temps[keyOf(e)]);
    if (missing.length) {
      toast.error(`Bitte Temperatur eintragen: ${missing.slice(0, 3).map((m) => m.object_name).join(", ")}${missing.length > 3 ? " …" : ""}`);
      return;
    }
    // Vertipper-Schutz: Rückfrage bei Grenzwertüberschreitung
    const exceededSel = selectedEntries.filter((e) =>
      e.type === "temperature" && e.temp_limit != null && temps[keyOf(e)] !== "" &&
      parseFloat(temps[keyOf(e)]) > e.temp_limit);
    if (exceededSel.length) {
      const list = exceededSel.map((e) => `• ${e.object_name}: ${temps[keyOf(e)]} °C (max. ${e.temp_limit} °C)`).join("\n");
      const ok = window.confirm(
        `⚠ Achtung – folgende Temperaturen liegen ÜBER dem Grenzwert:\n\n${list}\n\n` +
        `Bitte prüfen, ob du dich vertippt hast.\nTrotzdem so speichern? (Es wird automatisch ein Vorfall angelegt.)`);
      if (!ok) return;
    }
    setSaving(true);
    try {
      const items = selectedEntries.map((e) => ({
        task_id: e.task_id, period_key: e.period_key,
        temperature: e.type === "temperature" ? parseFloat(temps[keyOf(e)]) : null,
      }));
      const { data } = await api.post("/todo/complete-bulk", { items, note: note || null });
      if (data.exceeded?.length) {
        toast.warning(`${data.created} gespeichert. Grenzwert überschritten bei: ${data.exceeded.join(", ")} – bitte Korrekturmaßnahme unter Vorfälle prüfen.`, { duration: 8000 });
      } else {
        toast.success(`${data.created} Aufgaben als erledigt gespeichert`);
      }
      await load();
    } catch { toast.error("Fehler beim Speichern"); } finally { setSaving(false); }
  };

  return (
    <div className="pb-24">
      <PageHeader title="Todo-Liste" subtitle="Fällige Hygiene- und Kontrolltätigkeiten – mehrere abhaken, einmal speichern">
        <Button data-testid="btn-spontan-todo" onClick={() => setSpontanOpen(true)} variant="outline">
          <Plus className="h-4 w-4 mr-1" /> Spontan-Todo
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-1 rounded-xl bg-white border border-slate-200 p-1 mb-4 w-fit">
        {TABS.map((t) => (
          <button key={t.id} data-testid={t.testid} onClick={() => { setTab(t.id); clearSel(); }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            {t.label}
            <span className={`rounded-full px-1.5 text-xs ${tab === t.id ? "bg-white/20" : "bg-slate-100"}`}>{counts[t.id]}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="input-search-tasks" placeholder="Suchen (Tätigkeit, Objekt, Raum) …" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        {isActionTab && current.length > 0 && (
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="btn-select-all">
            <CheckCheck className="h-4 w-4 mr-1" /> Alle auswählen
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>
      ) : current.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="todo-empty">
          <Check className="h-10 w-10 mx-auto mb-2 text-emerald-400" /> Keine Einträge
        </div>
      ) : (
        <div className="space-y-6">
          {groupByRoom(current).map(([room, entries]) => (
            <div key={room}>
              <h3 className="font-heading text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-teal-500" /> {room}
                <span className="text-slate-400 font-normal">({entries.length})</span>
              </h3>
              <div className="space-y-2">
                {entries.map((e, i) => (
                  <TodoRow
                    key={keyOf(e) + i} entry={e} tab={tab}
                    checked={!!selected[keyOf(e)]} onToggle={() => toggle(e)}
                    tempVal={temps[keyOf(e)] || ""} onTempChange={(v) => setTemps((s) => ({ ...s, [keyOf(e)]: v }))}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sticky Aktionsleiste */}
      {isActionTab && selectedEntries.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-72 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]" data-testid="bulk-action-bar">
          <div className="mx-auto max-w-6xl flex flex-wrap items-center gap-3">
            <span className="font-semibold text-slate-800 text-sm">{selectedEntries.length} ausgewählt</span>
            <Input placeholder="Bemerkung für alle (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 min-w-[180px] max-w-xs" data-testid="input-bulk-note" />
            <Button variant="ghost" size="sm" onClick={clearSel}>Abwählen</Button>
            <Button onClick={saveBulk} disabled={saving} data-testid="btn-bulk-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Erledigt speichern
            </Button>
          </div>
        </div>
      )}

      {spontanOpen && <SpontanDialog rooms={rooms} onClose={() => setSpontanOpen(false)} onDone={() => { setSpontanOpen(false); load(); }} />}
    </div>
  );
}

function TodoRow({ entry, tab, checked, onToggle, tempVal, onTempChange }) {
  const isTemp = entry.type === "temperature";
  const done = tab === "erledigt";
  const exceeded = isTemp && tempVal !== "" && entry.temp_limit != null && parseFloat(tempVal) > entry.temp_limit;

  return (
    <div className={`flex items-center gap-3 rounded-xl border bg-white px-4 py-3 transition-colors ${checked ? "border-teal-400 bg-teal-50/40" : "border-slate-200"}`} data-testid="todo-row">
      {!done ? (
        <Checkbox checked={checked} onCheckedChange={onToggle} data-testid="todo-complete-checkbox" className="shrink-0" />
      ) : (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-emerald-500 text-white"><Check className="h-4 w-4" /></span>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800 text-sm">{entry.task_name}</span>
          {isTemp && <Thermometer className="h-3.5 w-3.5 text-teal-500" />}
        </div>
        <div className="text-xs text-slate-500">{entry.object_name}{entry.method ? ` · ${entry.method}` : ""}</div>
      </div>

      {/* Inline-Temperatur bei Temperatur-Tätigkeiten */}
      {isTemp && !done && (
        <div className="flex items-center gap-2 shrink-0">
          {exceeded && (
            <span className="inline-flex items-center gap-1 rounded-md bg-red-500 px-2 py-1 text-xs font-bold text-white" data-testid="row-temp-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> über Grenzwert!
            </span>
          )}
          <Input type="number" step="0.1" value={tempVal} onChange={(e) => onTempChange(e.target.value)}
            placeholder="°C" data-testid="input-row-temp"
            className={`w-20 h-9 text-center ${exceeded ? "border-2 border-red-500 bg-red-50 text-red-700 font-semibold focus-visible:ring-red-500" : ""}`} />
          <span className="text-xs text-slate-400">°C</span>
        </div>
      )}

      <div className="flex items-center gap-2 shrink-0">
        {isTemp && entry.temp_limit != null && (
          <StatusBadge variant={exceeded ? "warning" : "teal"}>max. {entry.temp_limit}°C</StatusBadge>
        )}
        {done ? (
          entry.exceeded ? <StatusBadge variant="warning">{entry.temperature} °C ⚠</StatusBadge>
            : <StatusBadge variant="done">{entry.temperature != null ? `${entry.temperature} °C` : "erledigt"}</StatusBadge>
        ) : (
          <StatusBadge variant={tab === "ueberfaellig" ? "warning" : "due"}>{entry.period_label}</StatusBadge>
        )}
        <StatusBadge variant="neutral">{INTERVAL_LABELS[entry.interval] || entry.interval}</StatusBadge>
      </div>
    </div>
  );
}

function SpontanDialog({ rooms, onClose, onDone }) {
  const [name, setName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Bitte Tätigkeit angeben"); return; }
    setBusy(true);
    try {
      await api.post("/todo/spontan", { name, room_id: roomId || null, note: note || null, photo_file_id: null });
      toast.success("Spontan-Todo dokumentiert");
      onDone();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="spontan-dialog">
        <DialogHeader>
          <DialogTitle>Spontan-Todo dokumentieren</DialogTitle>
          <DialogDescription>Ungeplante, erledigte Tätigkeit erfassen</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Tätigkeit *</Label>
            <Input data-testid="input-spontan-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Sonderreinigung Lager" /></div>
          <div className="space-y-1.5"><Label>Raum</Label>
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger data-testid="select-spontan-room"><SelectValue placeholder="Raum wählen" /></SelectTrigger>
              <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Bemerkung</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button data-testid="btn-submit-spontan" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
